import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faCopy, faCheck, faUserPlus, faUsers } from '@fortawesome/free-solid-svg-icons';
import ReactSelect from 'components/base/ReactSelect';
import toast from 'react-hot-toast';
import axiosInstance from '../../helpers/api';

interface InviteCustomerModalProps {
  show: boolean;
  onHide: () => void;
  dealId?: string; // Made optional for lead invitations
  leadId?: string; // For direct lead invitations
  dealName?: string;
  leadDetails?: any; // For lead mode
  dealDetails?: any; // Pass full deal details to extract customer info
  mode?: 'deal' | 'lead'; // To distinguish between deal and lead invitations
  useDirectAccess?: boolean; // Whether to use direct access URLs
  agentUsername?: string; // Agent username for direct access URLs
}

const InviteCustomerModal: React.FC<InviteCustomerModalProps> = ({
  show,
  onHide,
  dealId,
  leadId,
  dealName,
  leadDetails,
  dealDetails,
  mode = 'deal'
}) => {
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [selectedCustomerIndex, setSelectedCustomerIndex] = useState(0);
  const [showLeadSelector, setShowLeadSelector] = useState(false);
  const [leads, setLeads] = useState<any[]>([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [attachingCustomers, setAttachingCustomers] = useState(false);

  // Fetch leads for selection
  useEffect(() => {
    if (show && showLeadSelector) {
      fetchLeads();
    }
  }, [show, showLeadSelector]);

  const fetchLeads = async () => {
    setLoadingLeads(true);
    try {
      // Limit to 100 most recent leads to prevent UI freeze
      const response = await axiosInstance.get('/leads', {
        params: { limit: 100, page: 1 }
      });
      const leadsData = response.data?.data?.leads || [];
      
      // Transform for ReactSelect
      const transformedLeads = leadsData.map((lead: any) => ({
        value: lead.id,
        label: `${lead.lead_first_name || ''} ${lead.lead_last_name || ''} ${lead.lead_email ? `- ${lead.lead_email}` : ''} ${lead.lead_phone ? `- ${lead.lead_phone}` : ''}`.trim(),
        data: lead
      }));
      
      setLeads(transformedLeads);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoadingLeads(false);
    }
  };

  // Pre-populate customer info from deal or lead
  useEffect(() => {
    if (show) {
      // Reset previous values when modal opens
      setBuyerEmail('');
      setBuyerName('');
      setSelectedLeadIds([]);
      setShowLeadSelector(false);
      setInviteLink('');
      
      if (mode === 'lead' && leadDetails) {
        // Lead mode - directly populate from lead details
        console.log('🔍 InviteCustomerModal - Lead mode with leadDetails:', leadDetails);
        const fullName = `${leadDetails.lead_first_name || ''} ${leadDetails.lead_last_name || ''}`.trim();
        const email = leadDetails.lead_email || '';
        setBuyerName(fullName);
        setBuyerEmail(email);
        setShowLeadSelector(false); // Never show lead selector in lead mode
      } else if (mode === 'deal' && dealDetails) {
        console.log('🔍 InviteCustomerModal - dealDetails:', dealDetails);
        console.log('🔍 InviteCustomerModal - customers:', dealDetails.customers);
        
        // Check if deal has attached customers
        if (dealDetails.customers && dealDetails.customers.length > 0) {
          setShowLeadSelector(false);
          // Pre-select the first customer
          const customer = dealDetails.customers[0];
          console.log('🔍 InviteCustomerModal - first customer:', customer);
          // customers ARE leads, so access properties directly
          const fullName = `${customer.lead_first_name || ''} ${customer.lead_last_name || ''}`.trim();
          const email = customer.lead_email || '';
          setBuyerName(fullName);
          setBuyerEmail(email);
          console.log('🔍 InviteCustomerModal - populated:', { name: fullName, email: email });
        } else {
          // No customers attached - show lead selector
          console.log('🔍 InviteCustomerModal - No customers found, showing lead selector');
          setShowLeadSelector(true);
        }
      }
    }
  }, [show, mode, leadDetails, dealDetails]);

  const handleAttachAndInvite = async () => {
    if (showLeadSelector && selectedLeadIds.length === 0) {
      toast.error('Please select at least one customer');
      return;
    }

    setAttachingCustomers(true);
    try {
      // First, attach the selected customers to the deal
      if (showLeadSelector && selectedLeadIds.length > 0) {
        const response = await axiosInstance.patch(`/deals/${dealId}`, {
          customers: selectedLeadIds.map(leadId => ({ lead_id: leadId }))
        });

        if (response.data.success) {
          toast.success('Customers attached to deal');
          
          // Get the first selected lead's info for invitation
          const firstLead = leads.find(l => l.value === selectedLeadIds[0]);
          if (firstLead?.data) {
            setBuyerName(`${firstLead.data.lead_first_name || ''} ${firstLead.data.lead_last_name || ''}`.trim());
            setBuyerEmail(firstLead.data.lead_email || '');
          }
        }
      }
      
      // Now create the invitation
      await handleInvite();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to attach customers');
    } finally {
      setAttachingCustomers(false);
    }
  };

  const handleInvite = async () => {
    if (!buyerEmail || !buyerName) {
      toast.error('Please enter both name and email');
      return;
    }

    setLoading(true);
    try {
      // Get current user info for direct access
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const userId = user?.id;
      
      // Check if we should use direct access URL (for lead mode)
      if (mode === 'lead' && leadId && userId) {
        // Generate direct access URL
        const baseUrl = window.location.hostname === 'localhost' 
          ? 'http://localhost:3000'
          : 'https://aisearchportal.com';
        const directUrl = `${baseUrl}/${userId}/${leadId}`;
        setInviteLink(directUrl);
        
        // Still create invitation record for email notification
        await axiosInstance.post('/customer-portal/invite-from-lead', {
          buyerEmail,
          buyerName,
          leadId
        });
        
        toast.success('Direct access link created! The customer will receive an email with instructions.');
      } else {
        // Use traditional invitation flow
        const endpoint = mode === 'lead' ? '/customer-portal/invite-from-lead' : '/customer-portal/invite';
        const payload = {
          buyerEmail,
          buyerName,
          ...(mode === 'lead' ? { leadId } : { dealId })
        };
        
        const response = await axiosInstance.post(endpoint, payload);

        if (response.data.success) {
          setInviteLink(response.data.invite.url);
          toast.success('Invitation sent! The customer will receive an email with access instructions.');
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    // Don't reset email/name on close in case they want to reopen
    setInviteLink('');
    setCopied(false);
    setShowLeadSelector(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header 
        closeButton
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
          borderBottom: '1px solid rgba(102, 126, 234, 0.2)'
        }}
      >
        <Modal.Title className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faEnvelope} className="text-primary" />
          Invite Customer to Portal
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {!inviteLink ? (
          <>
            <Alert variant="info" className="mb-4">
              <strong>Customer Portal Access</strong>
              <p className="mb-0 mt-2">
                Invite your buyer to access their personalized property portal where they can:
              </p>
              <ul className="mb-0 mt-2">
                <li>Search properties with interactive map views</li>
                <li>Save favorite properties</li>
                <li>Schedule property tours</li>
                <li>Chat with AI assistant for property questions</li>
              </ul>
            </Alert>

            <Form>
              {/* Show lead selector if no customers attached (only in deal mode) */}
              {showLeadSelector && mode === 'deal' && (
                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold">
                    <FontAwesomeIcon icon={faUsers} className="me-1" />
                    Select Customers to Attach
                  </Form.Label>
                  <ReactSelect
                    options={leads}
                    placeholder="Search and select customers..."
                    isMulti
                    isSearchable
                    isLoading={loadingLeads}
                    value={leads.filter(lead => selectedLeadIds.includes(lead.value))}
                    onChange={(selected: any) => {
                      const ids = selected?.map((s: any) => s.value) || [];
                      setSelectedLeadIds(ids);
                      
                      // Auto-populate first customer's info
                      if (selected && selected.length > 0) {
                        const firstLead = selected[0].data;
                        setBuyerName(`${firstLead.lead_first_name || ''} ${firstLead.lead_last_name || ''}`.trim());
                        setBuyerEmail(firstLead.lead_email || '');
                      } else {
                        setBuyerName('');
                        setBuyerEmail('');
                      }
                    }}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderWidth: '2px',
                        '&:hover': {
                          borderColor: '#6366f1'
                        }
                      })
                    }}
                  />
                  <Form.Text className="text-muted">
                    No customers are currently attached to this deal. Select one or more customers to attach and invite.
                  </Form.Text>
                </Form.Group>
              )}

              {/* Show customer selector if multiple customers attached */}
              {!showLeadSelector && dealDetails?.customers && dealDetails.customers.length > 1 && (
                <Form.Group className="mb-3">
                  <Form.Label>Select Customer to Invite</Form.Label>
                  <Form.Select
                    value={selectedCustomerIndex}
                    onChange={(e) => {
                      const index = parseInt(e.target.value);
                      setSelectedCustomerIndex(index);
                      const customer = dealDetails.customers[index];
                      if (customer) {
                        setBuyerName(`${customer.lead_first_name || ''} ${customer.lead_last_name || ''}`.trim());
                        setBuyerEmail(customer.lead_email || '');
                      }
                    }}
                  >
                    {dealDetails.customers.map((customer: any, index: number) => (
                      <option key={index} value={index}>
                        {`${customer.lead_first_name || ''} ${customer.lead_last_name || ''} ${customer.lead_email ? `(${customer.lead_email})` : ''}`.trim() || `Customer ${index + 1}`}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    This deal has multiple customers. Select which one to invite.
                  </Form.Text>
                </Form.Group>
              )}

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Customer Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter customer's full name"
                      value={buyerName}
                      onChange={(e) => setBuyerName(e.target.value)}
                      disabled={showLeadSelector && selectedLeadIds.length > 0}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Customer Email</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter customer's email address"
                      value={buyerEmail}
                      onChange={(e) => setBuyerEmail(e.target.value)}
                      disabled={showLeadSelector && selectedLeadIds.length > 0}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {dealName && (
                <Alert variant="light" className="border">
                  <small className="text-muted">
                    This invitation is for deal: <strong>{dealName}</strong>
                  </small>
                </Alert>
              )}
            </Form>
          </>
        ) : (
          <div className="text-center">
            <div className="mb-4">
              <div 
                className="d-inline-flex align-items-center justify-content-center"
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                }}
              >
                <FontAwesomeIcon icon={faCheck} size="2x" className="text-white" />
              </div>
            </div>

            <h5 className="mb-3">{inviteLink.includes('aisearchportal.com') && inviteLink.includes('/') && !inviteLink.includes('/invite/') ? 'Direct Access Link Created!' : 'Invitation Sent Successfully!'}</h5>
            <p className="text-muted mb-4">
              We've sent an email invitation to <strong>{buyerName}</strong> at <strong>{buyerEmail}</strong>.
              <br />
              {inviteLink.includes('aisearchportal.com') && inviteLink.includes('/') && !inviteLink.includes('/invite/') 
                ? 'They can access their personalized portal using this direct link.'
                : 'They will receive instructions to access their personalized property portal.'}
            </p>

            <div className="position-relative">
              <Form.Control
                type="text"
                value={inviteLink}
                readOnly
                className="text-center"
                style={{
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  padding: '12px 50px 12px 12px'
                }}
              />
              <Button
                variant="primary"
                size="sm"
                onClick={handleCopyLink}
                style={{
                  position: 'absolute',
                  right: '5px',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
              >
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="me-1" />
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>

            <Alert variant="warning" className="mt-4">
              <small>
                <strong>Note:</strong> {inviteLink.includes('aisearchportal.com') && inviteLink.includes('/') && !inviteLink.includes('/invite/') 
                  ? 'This direct access link provides immediate access to the portal with pre-populated buyer profile data.'
                  : 'This invitation link expires in 30 days. The customer will need to create an account using this link to access their portal.'}
              </small>
            </Alert>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!inviteLink ? (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            {showLeadSelector && selectedLeadIds.length > 0 ? (
              <Button 
                variant="primary" 
                onClick={handleAttachAndInvite}
                disabled={attachingCustomers || !buyerEmail || !buyerName}
              >
                {attachingCustomers ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Attaching & Inviting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                    Attach & Send Invitation
                  </>
                )}
              </Button>
            ) : (
              <Button 
                variant="primary" 
                onClick={handleInvite}
                disabled={loading || !buyerEmail || !buyerName}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Creating Invitation...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                    Send Invitation
                  </>
                )}
              </Button>
            )}
          </>
        ) : (
          <Button variant="primary" onClick={handleClose}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default InviteCustomerModal;